import JsonLd from '@/components/base/JsonLd';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AboutSection from './components/AboutSection';
import CategoryGrid from './components/CategoryGrid';
import TrustSection from './components/TrustSection';
import CTASection from './components/CTASection';
import ArticleHubSection from './components/ArticleHubSection';
import TestimonialSection from './components/TestimonialSection';

export default function Home() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://allchinabuyspreadsheet.wiki';
  const logoSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'AllChinaBuySpreadsheet',
    url: siteUrl,
    logo: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/14bc629c50cb04c08f38590896596985.jpeg',
    description: 'China Product Research Team providing curated and verified product spreadsheets with real-time updates, price comparisons, and trusted recommendations.',
  };
  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'AllChinaBuySpreadsheet',
    url: siteUrl,
    description: 'The most updated China product spreadsheet hub. Discover curated, verified, and trending product spreadsheets with real-time updates, price comparisons, and trusted recommendations for smarter buying.',
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={logoSchema} />
      <JsonLd data={webSiteSchema} />
      <Navbar />

      {/* Hero Section */}
      <header className="relative h-[600px] md:h-[700px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20digital%20workspace%20with%20holographic%20spreadsheet%20floating%20above%20sleek%20desk%2C%20futuristic%20data%20visualization%2C%20dark%20ambient%20background%20with%20neon%20accent%20highlights%2C%20cyberpunk%20professional%20aesthetic%2C%20glowing%20product%20icons%2C%20tech%20innovation%20atmosphere&width=1800&height=1200&seq=hero-main-v3&orientation=landscape"
          alt="AllChinaBuySpreadsheet hero background"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>

        <div className="relative z-10 w-full px-6 md:px-10 pt-20">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-2xl">
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-white leading-tight">
                AllChinaBuy<br />Spreadsheet
              </h1>
              <p className="text-lg md:text-xl text-white/80 mt-2 font-heading font-medium">
                The Most Updated
              </p>
              <p className="text-lg md:text-xl text-white/70 mt-1">
                China Product Spreadsheet Hub
              </p>
              <p className="text-base text-white/60 mt-8 max-w-lg leading-relaxed">
                Discover curated, verified, and trending China product spreadsheets with real-time updates, price comparisons, and trusted recommendations.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Explore Latest Spreadsheet
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/15 backdrop-blur-sm text-white text-sm font-medium rounded-full hover:bg-white/25 transition-colors whitespace-nowrap cursor-pointer border border-white/20"
                >
                  Compare Trending Products
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </header>

      <AboutSection />
      <CategoryGrid />
      <TrustSection />

      {/* Comparison Table Section */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-1.5 h-1.5 rounded-full bg-accent-500"></div>
              <span className="text-xs font-medium text-foreground-500 uppercase tracking-[0.2em]">
                How AllChinaBuySpreadsheet Compares
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
              Competitor Comparison
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-background-200">
                  <th className="text-left py-4 px-5 text-sm font-semibold text-foreground-800 whitespace-nowrap">Platform</th>
                  <th className="text-left py-4 px-5 text-sm font-semibold text-foreground-800 whitespace-nowrap">Ease of Use</th>
                  <th className="text-left py-4 px-5 text-sm font-semibold text-foreground-800 whitespace-nowrap">Price Level</th>
                  <th className="text-left py-4 px-5 text-sm font-semibold text-foreground-800 whitespace-nowrap">Trust Level</th>
                  <th className="text-left py-4 px-5 text-sm font-semibold text-foreground-800 whitespace-nowrap">Spreadsheet Support</th>
                  <th className="text-left py-4 px-5 text-sm font-semibold text-foreground-800 whitespace-nowrap">Best For</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-background-200/70 bg-primary-50/50">
                  <td className="py-4 px-5 text-sm font-semibold text-primary-700 whitespace-nowrap">AllChinaBuySpreadsheet</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Very Easy</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Low</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">High (curated)</td>
                  <td className="py-4 px-5 text-sm text-primary-600 font-medium whitespace-nowrap">Yes</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Beginners</td>
                </tr>
                <tr className="border-b border-background-200/70">
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">AliExpress</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Medium</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Medium</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Medium</td>
                  <td className="py-4 px-5 text-sm text-foreground-400 whitespace-nowrap">No</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">General buyers</td>
                </tr>
                <tr className="border-b border-background-200/70">
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Taobao</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Hard</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Very Low</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Medium</td>
                  <td className="py-4 px-5 text-sm text-foreground-400 whitespace-nowrap">No</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Advanced users</td>
                </tr>
                <tr className="border-b border-background-200/70">
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">DHgate</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Medium</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Low</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Medium</td>
                  <td className="py-4 px-5 text-sm text-foreground-400 whitespace-nowrap">No</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Bulk buyers</td>
                </tr>
                <tr>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">1688</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Hard</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Lowest</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Low</td>
                  <td className="py-4 px-5 text-sm text-foreground-400 whitespace-nowrap">No</td>
                  <td className="py-4 px-5 text-sm text-foreground-700 whitespace-nowrap">Wholesale</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 p-5 bg-accent-50 rounded-xl">
            <p className="text-sm text-foreground-700 leading-relaxed">
              <strong className="text-accent-700">SEO Insight:</strong> Spreadsheet-based shopping improves conversion
              clarity by 35–60%. Users prefer curated lists over raw marketplaces. Long-tail keyword dominance:
              &ldquo;best china spreadsheet products&rdquo; continues to grow as more global buyers seek structured
              product discovery tools.
            </p>
          </div>
        </div>
      </section>

      <CTASection />
      <ArticleHubSection />
      <TestimonialSection />
      <Footer />
    </div>
  );
}